import * as React from 'react';
import classNames from 'classnames';
import { VideoBoxCommonProps } from '../VideoBox.types';
import FillLayers from '../../FillLayers/viewer/FillLayers';
import { isCSSMaskSupported } from '../../../core/commons/utils';
import styles from './style/VideoBox.scss';
import { getVideoEvents, getVisibilityHandlers } from './eventHandlers';
import {
  parseSVGContentFromMaskImage,
  getSvgImageWithMask,
} from './maskedVideoBoxFallback';

const noMaskSupport = !isCSSMaskSupported();

const VideoBoxCommon: React.FC<VideoBoxCommonProps> = ({
  id,
  mediaControls,
  fillLayers,
  isMobileView,
  hasAudio,
  hasClick,
  hasRollIn,
  hasAudioRollIn,
  canClickPause,
  canRollPause,
  muted,
  autoplay,
  canReplay,
  hasMask,
  alt = '',
}) => {
  const playerRef = React.useRef<HTMLDivElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isMasked, setIsMasked] = React.useState(hasMask);
  // set the flag on state for render-related stuff
  const [isPosterOnly, setIsPosterOnly] = React.useState(false);
  // calculate local value for side-effects
  const currentIsPosterOnly = !!(hasMask && noMaskSupport);

  const {
    onClick = undefined,
    onMouseEnter = undefined,
    onMouseLeave = undefined,
    onMouseMove = undefined,
    onTouchEnd = undefined,
    mediaHandler = undefined,
    setAudioContext = undefined,
    MEDIA_EVENTS = [],
  } = isPosterOnly
    ? {}
    : getVideoEvents({
        playerRef,
        videoRef,
        audioSelector: `[data-audio-for="${id}"]`,
        isMobileView,
        autoplay,
        hasAudio,
        hasClick,
        hasRollIn,
        hasAudioRollIn,
        canClickPause,
        canRollPause,
        canReplay,
      });

  React.useEffect(() => {
    // update state with local value
    setIsPosterOnly(currentIsPosterOnly);

    if (!currentIsPosterOnly && (hasAudio || hasClick)) {
      return getVisibilityHandlers({
        playerRef,
        videoRef,
      });
    }
    return;
  }, [currentIsPosterOnly, hasAudio, hasClick, playerRef, videoRef]);

  React.useEffect(() => {
    MEDIA_EVENTS.forEach(eventType => {
      if (!(videoRef.current && mediaHandler)) {
        return;
      }

      if (
        eventType === 'loadeddata' &&
        videoRef.current.readyState >= videoRef.current.HAVE_CURRENT_DATA
      ) {
        mediaHandler.handleEvent({ type: eventType } as Event);
      } else {
        videoRef.current.addEventListener(eventType, mediaHandler);
      }
    });

    if (setAudioContext) {
      setAudioContext();
    }
  }, [videoRef, setAudioContext, mediaHandler, MEDIA_EVENTS]);

  /*
   * Create CSS mask fallback if needed (Edge < 18)
   */
  React.useLayoutEffect(() => {
    const playerEl = playerRef.current;

    // implies: browser only && has mask && css mask not supported
    if (currentIsPosterOnly && fillLayers.video && playerEl) {
      const style = window.getComputedStyle(playerEl);
      // read and parse the SVG content from the mask custom property
      const svgContent = parseSVGContentFromMaskImage(
        style.getPropertyValue('--mask-image'),
      );

      // mask the poster image
      fillLayers.video.posterImageInfo.maskDataElementString = getSvgImageWithMask(
        {
          svgContent,
          compId: id,
          alt: fillLayers.video.posterImageInfo.alt || alt,
        },
      );
      // disable video
      fillLayers.video.isVideoEnabled = false;

      // rerender and make the fallback visible
      setIsMasked(false);
    }
  }, [alt, id, fillLayers.video, currentIsPosterOnly]);

  return (
    <div
      id={id}
      ref={playerRef}
      className={classNames(styles.videobox, { [styles.masked]: isMasked })}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onTouchEnd={onTouchEnd}
      data-audio={muted ? 'off' : 'on'}
      data-has-play={!isPosterOnly && (hasClick || hasRollIn) ? '' : undefined}
      data-playing={!isPosterOnly && autoplay ? '' : undefined}
      data-stop={isPosterOnly ? '' : undefined}
    >
      <div className={styles.videoboxContainer}>
        <FillLayers
          {...fillLayers}
          videoRef={videoRef}
          extraClass={styles.videoboxFill}
        />
      </div>
      {!isPosterOnly ? mediaControls : null}
    </div>
  );
};

export default VideoBoxCommon;
